/**
 * Converts a string to a URL/HTML-id-safe slug by replacing any character
 * that is not alphanumeric or a hyphen with a hyphen.
 */
export const slugify = (str) => str.replace(/[^a-zA-Z0-9-]/g, '-')

export const convertSnakeToStartCase = (text) =>
  text
    ?.toLowerCase()
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')

export const convertCamelToStartCase = (str) =>
  str
    ?.replace(/(?:^\w|[A-Z]|\b\w)/g, (word) => ` ${word.toUpperCase()}`)
    .replace(/\s+/g, ' ')

export const convertStringToCamelCase = (str) => {
  str.replace(/[-_]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
}

export const convertDashToStartCase = (text) =>
  text
    ?.toLowerCase()
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')

export const convertAllUpperCaseToStartCase = (str) =>
  str?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())

export const convertSnakeToCamelCase = (text) =>
  text?.toLowerCase().split('_')
  .map((s, index) =>index === 0? s: s.charAt(0).toUpperCase() + s.slice(1)).join('');

export const formatCountryName = (countryName) => {
  // countries are in format of uppercase with underscores & with 'and' or 'of
  if (!countryName) return ''
  const lowercaseWords = ['and', 'of', 'the']
  const formattedCountryName = countryName
    .toLowerCase()
    .split('_')
    .map((word, index) => {
      if (index === 0 || !lowercaseWords.includes(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1)
      }
      return word
    })
  return formattedCountryName.join(' ')
}

export const mapValueToLabel = (constantsList, value, locale) => {
  const selectedField = constantsList?.find((field) => field.value === value)
  return selectedField ? selectedField[locale] : ''
}

export const PHONE_REG_EXP = /^\+?1?\d{9,15}$/

export const URL_REG_EXP =
  /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/

export const MAX_FILE_SIZE = 20000000

export const SUPPORTED_FORMATS = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.ms-word.document.macroEnabled.12',
  'application/vnd.ms-word.template.macroEnabled.12',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]

export const KEY_SUPPORTED_FORMATS = {
  pdf: 'application/pdf',
  dot: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  dotx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  docm: 'application/vnd.ms-word.document.macroEnabled.12',
  dotm: 'application/vnd.ms-word.template.macroEnabled.12',
  jpg: 'image/jpeg',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
}

export const ALLOWED_RESUME_FORMATS = [
  KEY_SUPPORTED_FORMATS.pdf,
  KEY_SUPPORTED_FORMATS.docx,
  KEY_SUPPORTED_FORMATS.dot,
  KEY_SUPPORTED_FORMATS.dotx,
  KEY_SUPPORTED_FORMATS.docm,
  KEY_SUPPORTED_FORMATS.dotm,
]

export const CSV_SUPPORTED_FORMATS = ['text/csv']

export const getFormattedDate = (inputDate) => {
  // day-month-year
  const date = new Date(inputDate)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const formattedDay = day < 10 ? `0${day}` : day
  const formattedMonth = month < 10 ? `0${month}` : month
  return `${formattedDay}-${formattedMonth}-${year}`
}

export const formatDateToReadableString = (inputDate) => {
  // day monthname, year
  const date = new Date(inputDate)
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const day = date.getDate()
  const monthIndex = date.getMonth()
  const year = date.getFullYear()
  return `${day} ${monthNames[monthIndex]}, ${year}`
}

/**
 * Formats a date value for display in a table cell.
 * Accepts YYYY-MM-DD, YYYY-MM-DD HH:MM:SS, ISO 8601, or any parseable date string.
 * Returns YYYY-MM-DD using the local timezone. Returns the original value if not parseable.
 */
export const formatTableDate = (value) => {
  if (!value) return value
  if (typeof value !== 'string' && typeof value !== 'number') return value
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function toSnakeCase(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

export function convertKeysToSnakeCase(obj) {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToSnakeCase(item))
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = toSnakeCase(key)
      acc[snakeKey] = convertKeysToSnakeCase(obj[key])
      return acc
    }, {})
  }
  return obj
}

/**
 * Formats a manager display name for dropdowns/selects
 * Returns fullname if available, otherwise returns a temporary name with email
 * @param {Object} manager - Manager object with user property
 * @param {Function} t - Translation function
 * @returns {string} Formatted display name
 */
export const formatManagerDisplayName = (manager, t) => {
  if (!manager?.user) return ''
  return manager.user.fullname 
    ? manager.user.fullname 
    : manager.user.email 
      ? `(${t('user.userFullNameTemp')}) ${manager.user.email}` 
      : ''
}

/**
 * Formats a Unix timestamp into a WhatsApp-style display string
 * Shows time for today, "Yesterday" with time for yesterday,
 * or a short date with time for older messages
 * @param {number} unixTimestamp - Unix timestamp in seconds
 * @returns {string} Formatted timestamp string
 */
export const formatWhatsAppTimestamp = (unixTimestamp) => {
  if (!unixTimestamp) return '';
  const date = new Date(unixTimestamp * 1000);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
  const timeString = date.toLocaleTimeString([], timeOptions);

  if (isToday) {
    return `Today, ${timeString}`;
  } else if (isYesterday) {
    return `Yesterday, ${timeString}`;
  } else {
    const dateOptions = { month: '2-digit', day: '2-digit', year: '2-digit' };
    return `${date.toLocaleDateString([], dateOptions)}, ${timeString}`;
  }
};